import { useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { useAppContext } from "../../../contexts/app";

const _timeToRefreshInMinuts = 15;


const updateHourRangeStatus = async (data) => {
	const currentTime = new Date();
	data.forEach(item => {
		const startTime = new Date();
		const endTime = new Date();

		// Split by colon and space - format is "HH:MM:SS AM/PM"
		const startParts = item['Start Time'].split(/[:\s]/);
		const endParts = item['End Time'].split(/[:\s]/);

		const startHour = startParts[0];
		const startMinute = startParts[1];
		const startPeriod = startParts[3]; // Period is at index 3 (after seconds)

		const endHour = endParts[0];
		const endMinute = endParts[1];
		const endPeriod = endParts[3]; // Period is at index 3 (after seconds)

		// Convert to 24-hour format properly
		let start24Hour = parseInt(startHour);
		if (startPeriod === 'PM' && start24Hour !== 12) {
			start24Hour += 12;
		} else if (startPeriod === 'AM' && start24Hour === 12) {
			start24Hour = 0;
		}

		let end24Hour = parseInt(endHour);
		if (endPeriod === 'PM' && end24Hour !== 12) {
			end24Hour += 12;
		} else if (endPeriod === 'AM' && end24Hour === 12) {
			end24Hour = 0;
		}

		startTime.setHours(start24Hour);
		startTime.setMinutes(parseInt(startMinute));
		startTime.setSeconds(0);

		endTime.setHours(end24Hour);
		endTime.setMinutes(parseInt(endMinute));
		endTime.setSeconds(0);

		// if endTime is less than startTime, add 1 day to endTime
		if (endTime < startTime) {
			endTime.setDate(endTime.getDate() + 1);
		}

		if (currentTime >= startTime && currentTime <= endTime) {
			item.status = 'within';
		} else if (currentTime < startTime) {
			item.status = 'before';
		} else {
			item.status = 'after';
		}
	}
	);
	return data;
}



const getTotalGoal = (dataObj) => {
    return dataObj.reduce((total, entry) => total + parseInt(entry.Goal),
    0);
}

const getTotalActual = (dataObj) => {
    return dataObj.reduce((total, entry) => total + parseInt(entry.Actual),
    0);
}

const getCumulativeGoalUpToNow = (dataObj) => {
	// Sum the goals only for time periods that have passed or are current (not "before")
	return dataObj.reduce((total, entry) => {
		if (entry.status !== 'before') {
			return total + parseInt(entry.Goal);
		}
		return total;
	}, 0);
}

const getCumulativeActualUpToNow = (dataObj) => {
	// Sum the actuals only for time periods that have passed or are current (not "before")
	return dataObj.reduce((total, entry) => {
		if (entry.status !== 'before') {
			return total + parseInt(entry.Actual);
		}
		return total;
	}, 0);
}

export default function ProductionLineTracking() {
	const { switchSpreadsheet, loadSheetAsJson, loadSheetsBatchAsJson } = useAppContext();
	const date = new Date();
	const [goals, setGoals] = useState(null);
	const [todaysGoal, setTodaysGoal] = useState(null);
	const [today, setToday] = useState(`${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`);
	const [goal, setGoal] = useState(null);
	const [onTrack, setOnTrack] = useState(true);
	const [actuals, setActuals] = useState(0);
	const [cumulativeGoalUpToNow, setCumulativeGoalUpToNow] = useState(0);
	const [cumulativeActualUpToNow, setCumulativeActualUpToNow] = useState(0);

	useEffect(() => {
		// Compare cumulative actual vs cumulative goal up to current time
		setOnTrack(cumulativeActualUpToNow >= cumulativeGoalUpToNow);
	}, [cumulativeActualUpToNow, cumulativeGoalUpToNow]);


	useEffect(() => {

		let retryTimeout = null;

		const loadAllData = async () => {
			try {
				// Load both spreadsheets using the batch loader which handles switching correctly
				const results = await loadSheetsBatchAsJson(
					[process.env.REACT_APP_ONTRACK, process.env.REACT_APP_HourByHour],
					{ force: true, restore: true }
				);
				const goalsResult = await results.find(r => r.spreadsheetId === process.env.REACT_APP_HourByHour);
				const goalsData = goalsResult?.data || [];

				// Normalize field names - remove trailing spaces from keys
				const normalizedData = goalsData.map(item => {
					const normalized = {};
					Object.keys(item).forEach(key => {
						normalized[key.trim()] = item[key];
					});
					return normalized;
				});

				const temp = await updateHourRangeStatus(normalizedData);
				setGoals(temp);
				setActuals(getTotalActual(normalizedData));
				setTodaysGoal(getTotalGoal(normalizedData));
				setCumulativeGoalUpToNow(getCumulativeGoalUpToNow(temp));
				setCumulativeActualUpToNow(getCumulativeActualUpToNow(temp));
				console.log('Loaded goals data:', JSON.stringify(normalizedData, null, 4));

			} catch (error) {
				console.error('Error loading data:', error);
			}
		};

		loadAllData();

		const interval = setInterval(loadAllData, _timeToRefreshInMinuts * 1000 * 60); // Refresh every defined minutes

		return () => {
			clearInterval(interval);
			if (retryTimeout) clearTimeout(retryTimeout);
		};

	}, []);



	return goals && <>
		<div className={`${onTrack ? 'tw-bg-green-500 tw-text-black' : 'tw-bg-red-600 tw-text-white'} tw-flex tw-flex-col tw-justify-center tw-leading-none tw-text-center tw-items-center tw-font-quaternary tw-font-quaternary-thin tw-tracking-wide tw-h-screen tw-w-full`}>
			<div className="Xtw-space-y-40d Xtw-pt-80d tw-w-full tw-px-40d tw-flex tw-flex-col tw-flex-grow">

				<div className="tw-flex tw-flex-col tw-justify-center tw-py-10px tw-w-full tw-flex-grow">
					<div className="tw-w-full tw-flex tw-flex-col tw-justify-evenly tw-flex-grow">
						{goals?.map((goalItem, index) => {

							// console.log(`${goalItem["Start Time"]} - ${goalItem["End Time"]}: status=${goalItem?.status}, Goal=${goalItem.Goal}, Actual=${goalItem.Actual}`)

							return <div className="tw-w-full tw-border-b-3px tw-border-b-[currentColor]" key={index}>
								<div className="tw-flex tw-w-full tw-text-dynamic tw-uppercase tw-pb-[.5em]" style={ goalItem?.status === "within" ? { '--max-font-size': '30', '--min-font-size': '1' } : { '--max-font-size': '30', '--min-font-size': '1' }}>
									<div className="tw-flex-grow tw-basis-1/2 tw-text-left tw-inline-flex tw-items-center">
										{goalItem["Start Time"].replaceAll(":00 ", "")} - {goalItem["End Time"].replaceAll(":00 ", "")}
									</div>
									<div className="tw-flex-grow tw-basis-1/2 tw-text-right">
										<span className={`${
											goalItem?.status === "within" ? 'tw-bg-white tw-text-black' :
											goalItem?.status === "after" ? (goalItem.Actual >= goalItem.Goal ? 'tw-bg-green-900 tw-text-white' : 'tw-bg-red-900 tw-text-white') :
											''
										} tw-inline-flex  tw-px-10p tw-py-8p ${ goalItem?.status === "within" ? 'tw-min-w-250d' : 'tw-min-w-200d'} tw-justify-center`}>
											{goalItem?.status === "before" ? (
												<><span className="tw-font-quaternary-bold">{goalItem.Goal}</span></>
											) : (
												<><span className="tw-font-quaternary-bold">{goalItem.Actual}</span> <span className="tw-font-quaternary-thin">/</span> <span className="tw-font-quaternary-bold">{goalItem.Goal}</span></>
											)}
										</span>
									</div>
								</div>
								{goalItem.Reason && <div className="tw-flex tw-w-full tw-text-dynamic tw-my-5d tw-normal-case" style={{ '--max-font-size': '15', '--min-font-size': '1' }}>
									<span className="Xjs-marquee Xtw-whitespace-nowrap tw-flex-grow tw-basis-1/2 tw-text-right">
										{goalItem.Reason}
									</span>
								</div>}

							</div>
						})}
					</div>
				</div>
				<div className="tw-text-dynamic tw-font-quaternary-bold tw-leading-[.8] tw-flex tw-py-10px tw-items-center tw-justify-between tw-flex-shrink-0" style={{ '--max-font-size': '80', '--min-font-size': '1' }}>
					<span>
						<span className="tw-uppercase">
							Today: {` `}
						</span>
						<span>
							<span>{actuals}</span>
							<span className="tw-font-quaternary-thin">/</span>
							<span>{todaysGoal}</span>
						</span>
					</span>
					<span>
						<span className="tw-uppercase">
							Now: {` `}
						</span>
						<span>{actuals}</span>
						<span className="tw-font-quaternary-thin">/</span>
						<span>{getCumulativeGoalUpToNow(goals)}</span>
					</span>
				</div>
			</div>
		</div>
	</>
}
